/**
 * FleetFines — Module Amendes Parc Auto AI-first.
 * Import PDF → IA extrait tout → match véhicule/conducteur → notifier + suivre.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Receipt, Sparkles, Upload, Send, Settings2, Car, User, MapPin,
  Calendar, Euro, MinusCircle, AlertTriangle, ShieldAlert, CheckCircle2,
  FileText, FileDown, MoreVertical, Trash2, Loader2, ExternalLink, Info,
  ShieldCheck, UserCog, Save, X,
} from "lucide-react";
import { toast } from "sonner";
import FineImportWizard from "@/components/fleet/FineImportWizard";

const STATUS_META = {
  received: { label: "Reçue", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  notified: { label: "Conducteur notifié", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  acknowledged: { label: "Permis confirmé", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  designated: { label: "Conducteur désigné", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  paid: { label: "Payée", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  contested: { label: "Contestée", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  ignored: { label: "Ignorée", cls: "bg-slate-50 text-slate-500 border-slate-200" },
};

const fmtDate = (v) => v ? new Date(v).toLocaleDateString("fr-FR") : "—";
const fmtEuro = (v) => v != null ? `${Number(v).toFixed(2)} €` : "—";

export default function FleetFines() {
  const navigate = useNavigate();
  const [fines, setFines] = useState([]);
  const [stats, setStats] = useState({ total: 0, unpaid: 0, unnotified: 0, license_lost: 0, designation_due_soon: 0 });
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedFine, setSelectedFine] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/fleet/fines",
        { params: { status: filterStatus || undefined } });
      setFines(r.data.fines || []);
      setStats(r.data.stats || {});
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus]);

  const grouped = useMemo(() => {
    const g = { critical: [], active: [], closed: [] };
    fines.forEach(f => {
      if (f.driver_license_valid === false) g.critical.push(f);
      else if (["paid", "ignored", "designated"].includes(f.status)) g.closed.push(f);
      else g.active.push(f);
    });
    return g;
  }, [fines]);

  return (
    <div className="space-y-6" data-testid="fleet-fines-page">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            <Receipt className="w-6 h-6 text-slate-700" />
            Amendes Parc Auto
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Dépose le PDF · l'IA extrait tout · le conducteur est notifié en 1 clic
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" asChild data-testid="fines-csv-batch-btn">
            <a href="/api/fleet/fines/csv-batch" download>
              <FileText className="w-4 h-4 mr-1.5" /> Export CSV ANTAI
            </a>
          </Button>
          <Button variant="outline" onClick={() => setSettingsOpen(true)}
            data-testid="fines-settings-btn">
            <Settings2 className="w-4 h-4 mr-1.5" /> Réglages
          </Button>
          <Button onClick={() => setWizardOpen(true)}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="fines-import-btn">
            <Sparkles className="w-4 h-4 mr-1.5" /> Importer une amende
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Total" value={stats.total} icon={Receipt} color="slate" />
        <KpiCard label="Non payées" value={stats.unpaid} icon={Euro} color="amber" />
        <KpiCard label="À notifier" value={stats.unnotified} icon={Send} color="blue" />
        <KpiCard label="Désignation < 7 j" value={stats.designation_due_soon}
          icon={AlertTriangle} color="orange" hint="Art. L121-6" />
        <KpiCard label="Permis perdus" value={stats.license_lost}
          icon={ShieldAlert} color="red" />
      </div>

      {/* Bandeau critique */}
      {grouped.critical.length > 0 && (
        <Alert className="bg-red-50 border-red-200" data-testid="alert-license-lost">
          <ShieldAlert className="w-4 h-4 text-red-600" />
          <AlertDescription className="text-sm text-red-800">
            <strong>{grouped.critical.length} conducteur(s)</strong> ont signalé leur
            permis comme non valide — action RH immédiate requise.
          </AlertDescription>
        </Alert>
      )}

      {/* Filtre */}
      <div className="flex items-center gap-3">
        <Select value={filterStatus || "__all__"}
          onValueChange={(v) => setFilterStatus(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="filter-status">
            <SelectValue placeholder="Filtrer par statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tous statuts</SelectItem>
            {Object.entries(STATUS_META).map(([v, m]) =>
              <SelectItem key={v} value={v}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded animate-pulse" />)}
        </div>
      ) : fines.length === 0 ? (
        <Card><CardContent className="py-16 text-center">
          <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Aucune amende enregistrée.</p>
          <Button onClick={() => setWizardOpen(true)} className="mt-4"
            data-testid="empty-import-btn">
            <Sparkles className="w-4 h-4 mr-1.5" /> Importer la première amende
          </Button>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {grouped.critical.length > 0 && (
            <FineGroup title="⚠ Alertes permis conducteur"
              items={grouped.critical} onOpen={setSelectedFine} tone="critical" />
          )}
          {grouped.active.length > 0 && (
            <FineGroup title="En cours de traitement"
              items={grouped.active} onOpen={setSelectedFine} tone="active" />
          )}
          {grouped.closed.length > 0 && (
            <FineGroup title="Clôturées"
              items={grouped.closed} onOpen={setSelectedFine} tone="closed" />
          )}
        </div>
      )}

      <FineImportWizard open={wizardOpen} onOpenChange={setWizardOpen}
        onCreated={load} />
      <FineSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      <FineDetailDialog fine={selectedFine} onOpenChange={(v) => !v && setSelectedFine(null)}
        onChange={load} />
    </div>
  );
}

// ---------------------------------------------------------------------------
function KpiCard({ label, value, icon: Icon, color, hint }) {
  const COLORS = {
    slate: "bg-slate-100 text-slate-700",
    amber: "bg-amber-100 text-amber-700",
    blue: "bg-blue-100 text-blue-700",
    orange: "bg-orange-100 text-orange-700",
    red: "bg-red-100 text-red-700",
  };
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                 transition={{ duration: 0.25 }}>
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${COLORS[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="text-2xl font-bold text-slate-900 font-mono">{value ?? 0}</div>
          <div className="text-xs text-slate-500 mt-0.5">{label}</div>
          {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function FineGroup({ title, items, onOpen, tone }) {
  const toneCls = tone === "critical" ? "border-red-200"
                : tone === "closed" ? "opacity-70"
                : "";
  return (
    <Card className={toneCls}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-slate-700 flex items-center gap-2">
          {title}
          <Badge variant="outline" className="text-[10px]">{items.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {items.map((f) => (
            <FineRow key={f.id} fine={f} onOpen={() => onOpen(f)} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function FineRow({ fine, onOpen }) {
  const meta = STATUS_META[fine.status] || STATUS_META.received;
  const amount = fine.amount_normal ?? fine.amount_reduced ?? 0;
  return (
    <button onClick={onOpen}
      className="w-full grid grid-cols-12 items-center gap-2 p-3 hover:bg-slate-50 text-left"
      data-testid={`fine-row-${fine.id}`}>
      <div className="col-span-3 flex items-center gap-2">
        <Car className="w-4 h-4 text-slate-400 shrink-0" />
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 text-sm font-mono">{fine.plate || "—"}</div>
          <div className="text-[11px] text-slate-500 truncate">
            {[fine.vehicle_brand, fine.vehicle_model].filter(Boolean).join(" ") || "Véhicule inconnu"}
          </div>
        </div>
      </div>
      <div className="col-span-3 min-w-0">
        <div className="text-slate-900 text-sm truncate">{fine.reason || "—"}</div>
        <div className="text-[11px] text-slate-500 flex items-center gap-1">
          <Calendar className="w-3 h-3" /> {fmtDate(fine.infraction_date)}
          {fine.location && <><span>·</span><MapPin className="w-3 h-3" /><span className="truncate">{fine.location}</span></>}
        </div>
      </div>
      <div className="col-span-2 min-w-0">
        {fine.employee_name ? (
          <>
            <div className="text-sm text-slate-800 flex items-center gap-1">
              <User className="w-3 h-3" /> {fine.employee_name}
            </div>
            <div className="text-[11px] text-slate-500 truncate">{fine.employee_email || ""}</div>
          </>
        ) : (
          <span className="text-xs text-amber-700">Conducteur non affecté</span>
        )}
      </div>
      <div className="col-span-2 text-right">
        <div className="font-mono font-semibold text-slate-900 text-sm">{fmtEuro(amount)}</div>
        {fine.points_lost ? (
          <div className="text-[11px] text-red-600 flex items-center justify-end gap-1">
            <MinusCircle className="w-3 h-3" /> {fine.points_lost} pt(s)
          </div>
        ) : null}
      </div>
      <div className="col-span-2 flex flex-col items-end gap-1">
        <Badge variant="outline" className={meta.cls}>{meta.label}</Badge>
        {fine.driver_license_valid === false && (
          <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200 text-[10px]">
            ⚠ Permis perdu
          </Badge>
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
function FineSettingsDialog({ open, onOpenChange }) {
  const [autoNotify, setAutoNotify] = useState(false);
  const [manager, setManager] = useState("");
  const [cc, setCc] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) return; (async () => {
    try {
      const r = await api.get("/fleet/fines/settings");
      setAutoNotify(!!r.data.auto_notify);
      setManager(r.data.manager_email || "");
      setCc((r.data.manager_cc_emails || []).join(", "));
    } catch (e) { toast.error(apiError(e)); }
  })(); }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/fleet/fines/settings", {
        auto_notify: autoNotify, manager_email: manager || null,
        manager_cc_emails: cc.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Réglages enregistrés");
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-4 h-4" /> Réglages amendes
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <label className="flex items-start gap-3 p-3 border rounded-md cursor-pointer hover:bg-slate-50">
            <input id="setting-auto-notify" name="setting-auto-notify"
              type="checkbox" checked={autoNotify}
              onChange={(e) => setAutoNotify(e.target.checked)}
              className="mt-1" data-testid="setting-auto-notify" />
            <div>
              <div className="font-medium text-slate-800">Notification automatique du conducteur</div>
              <div className="text-xs text-slate-500 mt-0.5">
                Dès qu'une amende est importée et qu'un véhicule + conducteur sont
                identifiés, l'email "confirme ton permis" est envoyé sans clic manuel.
              </div>
            </div>
          </label>
          <div>
            <Label className="text-xs uppercase text-slate-500">Email du manager RH/flotte</Label>
            <Input value={manager} onChange={(e) => setManager(e.target.value)}
              placeholder="rh@monentreprise.fr" type="email"
              data-testid="setting-manager-email" />
            <p className="text-[11px] text-slate-500 mt-1">
              Utilisé pour recevoir les alertes critiques (permis perdu).
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Copie (CC, virgules)</Label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)}
              placeholder="direction@monentreprise.fr" data-testid="setting-cc" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={busy}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="setting-save">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
function FineDetailDialog({ fine, onOpenChange, onChange }) {
  const [notifying, setNotifying] = useState(false);
  const [driverInfoOpen, setDriverInfoOpen] = useState(false);
  const [driverInfo, setDriverInfo] = useState(null);
  const [cerfaGateOpen, setCerfaGateOpen] = useState(false);

  useEffect(() => {
    if (!fine?.assigned_employee_id) { setDriverInfo(null); return; }
    (async () => {
      try {
        const r = await api.get(`/hr/employees/${fine.assigned_employee_id}`);
        setDriverInfo(r.data.employee);
      } catch (e) { setDriverInfo(null); }
    })();
  }, [fine?.assigned_employee_id, driverInfoOpen]);

  if (!fine) return null;
  const meta = STATUS_META[fine.status] || STATUS_META.received;

  const hasFullDriverInfo = driverInfo
    && driverInfo.driver_license_number
    && driverInfo.driver_license_issued_at
    && driverInfo.birth_place
    && driverInfo.address?.street;

  const notify = async () => {
    setNotifying(true);
    try {
      await api.post(`/fleet/fines/${fine.id}/notify-driver`);
      toast.success("Email envoyé au conducteur");
      onChange?.(); onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setNotifying(false); }
  };
  const action = async (path, msg) => {
    try { await api.post(`/fleet/fines/${fine.id}/${path}`);
      toast.success(msg); onChange?.(); onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
  };

  const cerfaHref = `/api/fleet/fines/${fine.id}/cerfa-15495`;
  const handleCerfaClick = (e) => {
    if (!hasFullDriverInfo) {
      e.preventDefault();
      setCerfaGateOpen(true);
    }
  };

  return (
    <Dialog open={!!fine} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Receipt className="w-5 h-5" />
            Amende {fine.reference || fine.id.slice(0, 8)}
            <Badge variant="outline" className={meta.cls}>{meta.label}</Badge>
            {fine.driver_license_valid === false && (
              <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200">
                ⚠ Permis perdu
              </Badge>
            )}
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <Info2 label="Véhicule" value={`${fine.plate || "—"} · ${[fine.vehicle_brand, fine.vehicle_model].filter(Boolean).join(" ") || "—"}`} />
            <Info2 label="Conducteur" value={fine.employee_name || "Non affecté"} />
            <Info2 label="Date infraction" value={`${fmtDate(fine.infraction_date)}${fine.infraction_time ? ` · ${fine.infraction_time}` : ""}`} />
            <Info2 label="Lieu" value={fine.location || "—"} />
            <Info2 label="Motif" value={fine.reason || "—"} />
            <Info2 label="NATINF" value={fine.natinf || "—"} />
            <Info2 label="Montant" value={`Min : ${fmtEuro(fine.amount_reduced)} · Fort. : ${fmtEuro(fine.amount_normal)} · Maj. : ${fmtEuro(fine.amount_late)}`} />
            <Info2 label="Points" value={fine.points_lost ? `${fine.points_lost} pt(s)` : "—"} />
            <Info2 label="Paiement avant" value={fmtDate(fine.payment_deadline)} />
            <Info2 label="Désignation avant" value={fmtDate(fine.designation_deadline)} />
          </div>

          {driverInfo && !hasFullDriverInfo && (
            <Alert className="bg-amber-50 border-amber-200"
              data-testid="alert-driver-info-incomplete">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <AlertDescription className="text-xs text-amber-800 flex items-center justify-between gap-3">
                <span>
                  <strong>Infos conducteur incomplètes</strong> — permis, adresse ou lieu de naissance manquant.
                  Complète pour générer le Cerfa 15495 à 100%.
                </span>
                <Button size="sm" variant="outline"
                  className="border-amber-300 text-amber-800 h-7"
                  onClick={() => setDriverInfoOpen(true)}
                  data-testid="btn-complete-driver">
                  <UserCog className="w-3 h-3 mr-1" /> Compléter
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {fine.designation_deadline && !["designated", "contested", "ignored"].includes(fine.status) && (
            <Alert className="bg-amber-50 border-amber-200">
              <Info className="w-4 h-4 text-amber-600" />
              <AlertDescription className="text-xs text-amber-800">
                <strong>Obligation légale (art. L121-6)</strong> : l'employeur doit
                désigner le conducteur à l'ANTAI dans les 45 jours, sous peine
                d'amende personnelle (jusqu'à 3 750 €).
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2 flex-wrap">
            {fine.pdf_asset_id && (
              <Button variant="outline" size="sm" asChild data-testid="download-pdf">
                <a href={`/api/fleet/fines/${fine.id}/pdf`} target="_blank" rel="noreferrer">
                  <FileText className="w-4 h-4 mr-1.5" /> Ouvrir le PDF original
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </Button>
            )}
            {fine.assigned_employee_id && (
              <Button variant="outline" size="sm"
                className={hasFullDriverInfo
                  ? "border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                  : "border-amber-200 text-amber-700 hover:bg-amber-50"}
                asChild={hasFullDriverInfo}
                onClick={hasFullDriverInfo ? undefined : (e) => { e.preventDefault(); setCerfaGateOpen(true); }}
                data-testid="download-cerfa">
                {hasFullDriverInfo ? (
                  <a href={cerfaHref} download>
                    <FileDown className="w-4 h-4 mr-1.5" /> Générer Cerfa 15495 (100%)
                  </a>
                ) : (
                  <span>
                    <FileDown className="w-4 h-4 mr-1.5" /> Générer Cerfa 15495
                  </span>
                )}
              </Button>
            )}
            {fine.assigned_employee_id && (
              <Button variant="outline" size="sm"
                onClick={() => setDriverInfoOpen(true)}
                data-testid="btn-edit-driver-info">
                <UserCog className="w-4 h-4 mr-1.5" /> Infos conducteur
              </Button>
            )}
          </div>

          {fine.timeline && fine.timeline.length > 0 && (
            <div>
              <Label className="text-xs uppercase text-slate-500">Historique</Label>
              <div className="mt-2 space-y-1.5">
                {fine.timeline.slice().reverse().map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-600 border-l-2 border-slate-200 pl-3">
                    <span className="text-slate-400 font-mono">{new Date(t.at).toLocaleString("fr-FR")}</span>
                    <span>·</span>
                    <span className="font-medium">{t.label}</span>
                    {t.actor_email && <span className="text-slate-400">· {t.actor_email}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
          {fine.assigned_employee_id && fine.employee_email && ["received", "notified"].includes(fine.status) && (
            <Button variant="outline" onClick={notify} disabled={notifying}
              data-testid="notify-driver-btn">
              {notifying ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                          : <Send className="w-4 h-4 mr-1.5" />}
              Notifier le conducteur
            </Button>
          )}
          <Button variant="outline" onClick={() => action("mark-designated", "Conducteur désigné à l'ANTAI")}
            data-testid="mark-designated-btn">
            <User className="w-4 h-4 mr-1.5" /> Désigner à l'ANTAI
          </Button>
          <Button variant="outline" onClick={() => action("mark-contested", "Amende contestée")}
            data-testid="mark-contested-btn">
            Contester
          </Button>
          <Button onClick={() => action("mark-paid", "Amende marquée payée")}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="mark-paid-btn">
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Marquer payée
          </Button>
        </DialogFooter>

        <DriverInfoDialog open={driverInfoOpen}
          onOpenChange={setDriverInfoOpen}
          driver={driverInfo}
          onSaved={(d) => { setDriverInfo(d); onChange?.(); }} />
        <CerfaGateDialog open={cerfaGateOpen}
          onOpenChange={setCerfaGateOpen}
          driver={driverInfo}
          cerfaHref={cerfaHref}
          onOpenDriverInfo={() => { setCerfaGateOpen(false); setDriverInfoOpen(true); }} />
      </DialogContent>
    </Dialog>
  );
}

function Info2({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-slate-400 tracking-wide">{label}</div>
      <div className="text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DriverInfoDialog — édite permis + naissance + adresse d'un conducteur
// ---------------------------------------------------------------------------
function DriverInfoDialog({ open, onOpenChange, driver, onSaved }) {
  const [license, setLicense] = useState("");
  const [licenseDate, setLicenseDate] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [birthPlace, setBirthPlace] = useState("");
  const [street, setStreet] = useState("");
  const [postal, setPostal] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!driver) return;
    setLicense(driver.driver_license_number || "");
    setLicenseDate(driver.driver_license_issued_at || "");
    setBirthDate(driver.birth_date || "");
    setBirthPlace(driver.birth_place || "");
    const addr = driver.address || {};
    setStreet(addr.street || "");
    setPostal(addr.postal_code || "");
    setCity(addr.city || "");
  }, [driver, open]);

  const save = async () => {
    if (!driver) return;
    setBusy(true);
    try {
      const r = await api.patch(`/hr/employees/${driver.id}/driver-info`, {
        driver_license_number: license || null,
        driver_license_issued_at: licenseDate || null,
        birth_date: birthDate || null,
        birth_place: birthPlace || null,
        address: { street: street || null, postal_code: postal || null, city: city || null },
      });
      toast.success("Infos conducteur enregistrées");
      onSaved?.(r.data.employee);
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (!driver) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="w-4 h-4" /> Infos conducteur — {driver.first_name} {driver.last_name}
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="rounded-md bg-indigo-50 border border-indigo-200 p-3 text-xs text-indigo-900">
            Ces informations sont utilisées pour <strong>pré-remplir le Cerfa 15495</strong> et l'export CSV ANTAI. Modifiable côté RH ou Parc Auto — 1 seule source.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase text-slate-500">N° permis</Label>
              <Input value={license} onChange={(e) => setLicense(e.target.value)}
                placeholder="12AB34567" data-testid="driver-info-license" />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Date de délivrance</Label>
              <Input type="date" value={licenseDate}
                onChange={(e) => setLicenseDate(e.target.value)}
                data-testid="driver-info-license-date" />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Date de naissance</Label>
              <Input type="date" value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                data-testid="driver-info-birth-date" />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Lieu de naissance</Label>
              <Input value={birthPlace}
                onChange={(e) => setBirthPlace(e.target.value)}
                placeholder="Paris" data-testid="driver-info-birth-place" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Adresse</Label>
            <Input value={street} onChange={(e) => setStreet(e.target.value)}
              placeholder="14 rue des Lilas" className="mb-2"
              data-testid="driver-info-street" />
            <div className="grid grid-cols-3 gap-2">
              <Input value={postal} onChange={(e) => setPostal(e.target.value)}
                placeholder="75011" data-testid="driver-info-postal" />
              <Input value={city} onChange={(e) => setCity(e.target.value)}
                placeholder="Paris" className="col-span-2"
                data-testid="driver-info-city" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={busy}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="driver-info-save">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                     : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// CerfaGateDialog — s'ouvre quand permis manquant, propose de compléter d'abord
// ---------------------------------------------------------------------------
function CerfaGateDialog({ open, onOpenChange, driver, cerfaHref, onOpenDriverInfo }) {
  if (!driver) return null;
  const missing = [];
  if (!driver.driver_license_number) missing.push("N° de permis");
  if (!driver.driver_license_issued_at) missing.push("Date de délivrance");
  if (!driver.birth_place) missing.push("Lieu de naissance");
  if (!driver.address?.street) missing.push("Adresse");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-4 h-4" />
            Générer un Cerfa incomplet ?
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-slate-700">
            Certains champs du Cerfa 15495 ne peuvent pas être pré-remplis pour
            <strong> {driver.first_name} {driver.last_name}</strong> :
          </p>
          <ul className="text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded-md p-3 space-y-1">
            {missing.map(m => (
              <li key={m} className="flex items-center gap-1">
                <X className="w-3 h-3 text-amber-600" /> {m}
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-500">
            Complète maintenant pour un Cerfa 100% auto — sinon le PDF sera généré
            avec des lignes pointillées à remplir à la main.
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" asChild data-testid="cerfa-gate-download-anyway">
            <a href={cerfaHref} download onClick={() => onOpenChange(false)}>
              Télécharger quand même
            </a>
          </Button>
          <Button onClick={onOpenDriverInfo}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="cerfa-gate-complete">
            <UserCog className="w-4 h-4 mr-2" /> Compléter d'abord
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
